/**
 * The MIXED sheet's factory half — kept apart from the derivations so the
 * store (entry chunk) can create a sheet without pulling the class catalogue,
 * the backgrounds and the rest of the derivation graph into the entry bundle.
 * Re-exported from ./mixed-sheet, so every caller keeps its import.
 */
import type { AbilityId } from '@/content/dnd5e';
import { abilityMod, averageHitPoints } from '@/content/dnd5e';
import { LINEAGES, benderRow, type LineageId } from '@/content/bender-progression';
import type { AbilityScoreMap, MixedSheet } from './campaign';

/* --------------------------------------------------------------- factories */

export const MIXED_CALLINGS = ['bender', 'weaponsmaster', 'tech-engineer'] as const;
export type MixedCalling = (typeof MIXED_CALLINGS)[number];

export const CALLING_LABELS: Record<MixedCalling, { tr: string; en: string }> = {
  bender: { tr: 'Bükücü', en: 'Bender' },
  weaponsmaster: { tr: 'Silah Ustası', en: 'Weaponsmaster' },
  'tech-engineer': { tr: 'Teknik Mühendis', en: 'Tech-Engineer' },
};

/** Hit die for a calling (ATLA 5e Homebrew pp.11–26; Tech-Engineer d8). */
export function hitDieFor(lineage: string | null, calling: string): number {
  if (calling === 'bender' && lineage && lineage in LINEAGES) {
    return LINEAGES[lineage as LineageId].hitDie;
  }
  return calling === 'tech-engineer' ? 8 : 10;
}

/** Bending ability: the nation's, or the calling's key ability for non-benders. */
export function keyAbilityFor(lineage: string | null, calling: string): AbilityId {
  if (calling === 'bender' && lineage && lineage in LINEAGES) {
    return LINEAGES[lineage as LineageId].bendingAbility as AbilityId;
  }
  return calling === 'tech-engineer' ? 'int' : 'str';
}

/** Saving-throw proficiencies: the nation's fixed save plus one chosen. */
export function suggestedSaves(lineage: string | null, calling: string): AbilityId[] {
  const fixed =
    calling === 'bender' && lineage && lineage in LINEAGES
      ? (LINEAGES[lineage as LineageId].savingThrow as AbilityId)
      : calling === 'tech-engineer'
        ? 'con'
        : 'str';
  return [fixed, 'dex'];
}

export function emptyScores(): AbilityScoreMap {
  return { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
}

export function defaultMixedSheet(era: string | null, lineage: string | null = null): MixedSheet {
  const calling: MixedCalling = lineage ? 'bender' : 'weaponsmaster';
  const hitDie = hitDieFor(lineage, calling);
  const scores = emptyScores();
  const conMod = abilityMod(scores.con);
  return {
    kind: 'mixed',
    lineage,
    element: null,
    calling,
    level: 1,
    abilityScores: scores,
    abilityMethod: 'standard-array',
    abilityRolls: [],
    saveProficiencies: suggestedSaves(lineage, calling),
    skillProficiencies: [],
    lineageSkillChoices: [],
    backgroundId: null,
    origin: null,
    expertiseSkills: [],
    armorId: 'leather',
    shield: false,
    acOverride: null,
    hp: { current: averageHitPoints(hitDie, 1, conMod), max: averageHitPoints(hitDie, 1, conMod), temp: 0 },
    hitDie,
    speed: 30,
    inspiration: false,
    exhaustion: 0,
    deathSaves: { successes: 0, failures: 0 },
    conditions: [],
    techniqueIds: [],
    techniqueSlots: [
      { tier: 'practiced', count: benderRow(1).practiced },
      { tier: 'trained', count: benderRow(1).trained },
      { tier: 'mastered', count: benderRow(1).mastered },
    ],
    fightingTechniqueIds: [],
    featIds: [],
    gear: [],
    gold: null,
    weaponIds: ['quarterstaff'],
    aspects: [
      { label: 'high-concept', text: '' },
      { label: 'trouble', text: '' },
      { label: 'relationship', text: '' },
      { label: 'free', text: '' },
      { label: 'free', text: '' },
    ],
    fatePoints: 3,
    era,
    features: '',
    proficiencies: '',
    background: '',
    gender: '',
    age: '',
    height: '',
    personality: { traits: '', ideal: '', bond: '', flaw: '' },
    backgroundFeature: '',
    backgroundStory: '',
    saveConditional: '',
  };
}

